import React, { useState, useCallback, useMemo, useRef, useEffect, Key } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { SearchField, ActionButton, Picker, PickerItem, Text, NumberField, DatePicker, Button } from '@react-spectrum/s2';
import { CalendarDate } from '@internationalized/date';
import { Close, Filter } from '../../icons/s2';
import { DataTable } from '../DataTable';
import type { DataTableProps } from '../DataTable';
import type { FilterConfig, FiltersState } from './types';
import './FilterableTable.css';
// Spectrum dimension token -> CSS px. Runtime widths (from FilterConfig) can't
// feed the compile-time style() macro, so they're applied via UNSAFE_style.
function sizeTokenToCss(token: string): string {
    const match = /^size-(\d+)$/.exec(token);
    return match ? `${Number(match[1]) * 0.08}px` : token;
}
function formatChipValue(config: FilterConfig, raw: string, t: TFunction): string {
    const type = config.type ?? 'text';
    if (type === 'text') return raw;
    if (type === 'select') {
        const match = config.options?.find((opt)=>opt.value === raw);
        return match ? match.label : raw;
    }
    const [min, max] = raw.split('..');
    if (type === 'range_number') {
        if (min && max) return `${min} - ${max}`;
        if (min) return `>= ${min}`;
        if (max) return `<= ${max}`;
    }
    if (type === 'range_date') {
        if (min && max) return t('components.filterableTable.chip.dateRange', {
            min,
            max
        });
        if (min) return t('components.filterableTable.chip.dateFrom', {
            min
        });
        if (max) return t('components.filterableTable.chip.dateUntil', {
            max
        });
    }
    return raw;
}
// =============================================================================
// Range input sub-components (internal)
// =============================================================================
interface RangeNumberInputProps {
    onApply: (value: string) => void;
}
function RangeNumberInput({ onApply }: RangeNumberInputProps) {
    const { t } = useTranslation();
    const [min, setMin] = useState<number | undefined>(undefined);
    const [max, setMax] = useState<number | undefined>(undefined);
    const handleApply = useCallback(()=>{
        const minStr = min !== undefined ? String(min) : '';
        const maxStr = max !== undefined ? String(max) : '';
        if (!minStr && !maxStr) return;
        onApply(`${minStr}..${maxStr}`);
    }, [
        min,
        max,
        onApply
    ]);
    return (<div className={" sd171 ec171 Ue171 qe171"}>
      <NumberField aria-label={t('components.filterableTable.minimum')} label={t('components.filterableTable.min')} value={min} onChange={(v)=>setMin(isNaN(v) ? undefined : v)} styles={" ZH171"} hideStepper/>
      <NumberField aria-label={t('components.filterableTable.maximum')} label={t('components.filterableTable.max')} value={max} onChange={(v)=>setMax(isNaN(v) ? undefined : v)} styles={" ZH171"} hideStepper/>
      <Button variant="primary" onPress={handleApply} isDisabled={min === undefined && max === undefined}>
        {t('components.filterableTable.apply')}
      </Button>
    </div>);
}
interface RangeDateInputProps {
    onApply: (value: string) => void;
}
function RangeDateInput({ onApply }: RangeDateInputProps) {
    const { t } = useTranslation();
    const [from, setFrom] = useState<CalendarDate | null>(null);
    const [to, setTo] = useState<CalendarDate | null>(null);
    const handleApply = useCallback(()=>{
        const fromStr = from ? from.toString() : '';
        const toStr = to ? to.toString() : '';
        if (!fromStr && !toStr) return;
        onApply(`${fromStr}..${toStr}`);
    }, [
        from,
        to,
        onApply
    ]);
    return (<div className={" sd171 ec171 Ue171 qe171"}>
      <DatePicker aria-label={t('components.filterableTable.fromDate')} label={t('components.filterableTable.from')} value={from} onChange={setFrom} styles={" Zq171"}/>
      <DatePicker aria-label={t('components.filterableTable.toDate')} label={t('components.filterableTable.to')} value={to} onChange={setTo} styles={" Zq171"}/>
      <Button variant="primary" onPress={handleApply} isDisabled={!from && !to}>
        {t('components.filterableTable.apply')}
      </Button>
    </div>);
}
// =============================================================================
// FilterPanel (internal)
// =============================================================================
interface FilterPanelProps {
    filters: FilterConfig[];
    values: FiltersState;
    onFilterChange: (filters: FiltersState) => void;
    onClear: () => void;
}
const DEBOUNCE_MS = 400;
function FilterPanel({ filters, values, onFilterChange, onClear }: FilterPanelProps) {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [textInput, setTextInput] = useState('');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(()=>{
        return ()=>{
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);
    const activeFilters = useMemo(()=>Object.entries(values).filter(([, v])=>v.trim() !== '').map(([key, value])=>{
            const config = filters.find((f)=>f.key === key);
            return {
                key,
                value,
                label: config?.label ?? key,
                displayValue: config ? formatChipValue(config, value, t) : value
            };
        }), [
        values,
        filters,
        t
    ]);
    const selectedConfig = useMemo(()=>filters.find((f)=>f.key === selectedCategory), [
        filters,
        selectedCategory
    ]);
    const handleCategoryChange = useCallback((key: Key | null)=>{
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setSelectedCategory(key as string | null);
        setTextInput('');
    }, []);
    const applyFilter = useCallback((value: string)=>{
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!selectedCategory) return;
        if (!value.trim()) return;
        const next = {
            ...values,
            [selectedCategory]: value.trim()
        };
        onFilterChange(next);
        setTextInput('');
        setSelectedCategory(null);
    }, [
        selectedCategory,
        values,
        onFilterChange
    ]);
    const handleTextChange = useCallback((value: string)=>{
        setTextInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!selectedCategory) return;
        if (!value.trim()) {
            if (values[selectedCategory]) {
                const next = {
                    ...values,
                    [selectedCategory]: ''
                };
                onFilterChange(next);
            }
            return;
        }
        debounceRef.current = setTimeout(()=>{
            const next = {
                ...values,
                [selectedCategory]: value.trim()
            };
            onFilterChange(next);
        }, DEBOUNCE_MS);
    }, [
        selectedCategory,
        values,
        onFilterChange
    ]);
    const handleTextClear = useCallback(()=>{
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setTextInput('');
        if (selectedCategory && values[selectedCategory]) {
            const next = {
                ...values,
                [selectedCategory]: ''
            };
            onFilterChange(next);
        }
    }, [
        selectedCategory,
        values,
        onFilterChange
    ]);
    const removeFilter = useCallback((key: string)=>{
        const next = {
            ...values,
            [key]: ''
        };
        onFilterChange(next);
    }, [
        values,
        onFilterChange
    ]);
    const filterType = selectedConfig?.type ?? 'text';
    return (<div className={" sd171 _ta171 Ue171 qe171"} style={{
        padding: '.5rem 0'
    }}>
      <div className={" sd171 ec171 Ul171 ql171 _wb171"}>
        <Picker aria-label={t('components.filterableTable.filterCategory')} placeholder={t('components.filterableTable.filterPlaceholder')} selectedKey={selectedCategory} onSelectionChange={handleCategoryChange} styles={" Zr171"}>
          {filters.map((f)=>(<PickerItem key={f.key} id={f.key}>{f.label}</PickerItem>))}
        </Picker>

        {selectedConfig && filterType === 'text' && (<SearchField aria-label={t('components.filterableTable.filterBy', {
        label: selectedConfig.label
    })} placeholder={selectedConfig.placeholder ?? t('components.filterableTable.searchByPlaceholder', {
        label: selectedConfig.label.toLowerCase()
    })} value={textInput} onChange={handleTextChange} onSubmit={applyFilter} onClear={handleTextClear} UNSAFE_style={{
        width: sizeTokenToCss(selectedConfig.width ?? 'size-2400')
    }} autoFocus/>)}

        {selectedConfig && filterType === 'range_number' && (<RangeNumberInput onApply={applyFilter}/>)}

        {selectedConfig && filterType === 'range_date' && (<RangeDateInput onApply={applyFilter}/>)}

        {selectedConfig && filterType === 'select' && (<Picker aria-label={t('components.filterableTable.filterBy', {
        label: selectedConfig.label
    })} label={selectedConfig.label} placeholder={selectedConfig.placeholder ?? t('components.filterableTable.selectPlaceholder')} selectedKey={values[selectedConfig.key] || null} onSelectionChange={(key)=>applyFilter(String(key))} UNSAFE_style={{
        width: sizeTokenToCss(selectedConfig.width ?? 'size-2000')
    }}>
            {(selectedConfig.options ?? []).map((opt)=>(<PickerItem key={opt.value} id={opt.value}>{opt.label}</PickerItem>))}
          </Picker>)}
      </div>

      {activeFilters.length > 0 && (<div className={" sd171 eb171 Ue171 qe171 _wb171"}>
          <Filter size="S"/>
          {activeFilters.map((af)=>(<div key={af.key} className={" sd171 eb171 Ub171 qb171"} style={{
            background: 'var(--spectrum-gray-200)',
            borderRadius: 'var(--radius-800)',
            padding: '0.125rem 0.25rem 0.125rem 0.625rem',
            fontSize: '0.8125rem'
        }}>
              <Text UNSAFE_style={{
            color: 'var(--spectrum-gray-700)',
            fontSize: '0.75rem'
        }}>
                {af.label}:
              </Text>
              <Text UNSAFE_style={{
            fontWeight: 500,
            fontSize: '0.8125rem'
        }}>
                {af.displayValue}
              </Text>
              <ActionButton isQuiet onPress={()=>removeFilter(af.key)} aria-label={t('components.filterableTable.removeFilter', {
            label: af.label
        })} UNSAFE_style={{
            minWidth: 0,
            padding: '0 0.25rem'
        }}>
                <Close size="XS"/>
              </ActionButton>
            </div>))}
          <ActionButton isQuiet onPress={onClear} aria-label={t('components.filterableTable.clearAllFilters')}>
            <Text UNSAFE_style={{
        fontSize: '0.75rem',
        color: 'var(--spectrum-gray-600)'
    }}>
              {t('components.filterableTable.clearAll')}
            </Text>
          </ActionButton>
        </div>)}
    </div>);
}
// =============================================================================
// FilterableTable (exported)
// =============================================================================
interface FilterableTableProps<T> extends DataTableProps<T> {
    filterConfigs: FilterConfig[];
    filterValues: FiltersState;
    onFiltersChange: (filters: FiltersState) => void;
    onFiltersClear: () => void;
    hideFilters?: boolean;
}
export function FilterableTable<T>({ filterConfigs, filterValues, onFiltersChange, onFiltersClear, hideFilters = false, toolbarStart, ...dataTableProps }: FilterableTableProps<T>) {
    const filterPanel = hideFilters ? undefined : (<FilterPanel filters={filterConfigs} values={filterValues} onFilterChange={onFiltersChange} onClear={onFiltersClear}/>);
    // `toolbarStart` (e.g. an "Add" button) renders in front of — to the left
    // of — the filter selector, on the same header row.
    const combinedStart = toolbarStart || filterPanel ? (<div className={" sd171 eb171 Ul171 ql171 _wb171"} style={{
        width: '100%'
    }}>
        {toolbarStart}
        {filterPanel}
      </div>) : undefined;
    return (<div className="filterable-table">
      <DataTable {...dataTableProps} toolbarStart={combinedStart}/>
    </div>);
}
export type { FilterableTableProps };

import "macro-333749ef48cb56c02d2831bccd8b14b1c96b20693212ae80ee3ee0c8dcd4d3d8.css";
import "macro-e49b8743c42be691aeac015930b31badccafbce72f5b156e6148dcc7f93443da.css";
import "macro-e49b8743c42be691aeac015930b31badccafbce72f5b156e6148dcc7f93443da.css";
import "macro-333749ef48cb56c02d2831bccd8b14b1c96b20693212ae80ee3ee0c8dcd4d3d8.css";
import "macro-db3a0f06aff4effa0f43fe63cd25439993d03a72934127049c5d2aab293c56ac.css";
import "macro-db3a0f06aff4effa0f43fe63cd25439993d03a72934127049c5d2aab293c56ac.css";
import "macro-cd70917bbc5b5938410f1c1ccfc163c6e832ad8bc4e3938f00ea88bd9e68339f.css";
import "macro-9633e7d07b931656e340a0819cda826cee3ee03c7d20021c17e3b909b0ad03b5.css";
import "macro-78eb2ccc70a1f2ef076b4459211cabfd4dd5944208a4aa94ae911a0aa100ea61.css";
import "macro-2e77db67eee3cb608f30e6dd85a32ec715ce97641e1fb443ac03bcbf2031d800.css";
import "macro-236893de8a979158ae6c73bae4dbfc01999c61ef6035f0dd35d56d07972e4cfa.css";
import "macro-d847c8e2985449fdb31a984a0e64c94f71dc278351b8018c1132f86b1a231fba.css";